"use client";

import { useEffect } from "react";
import { ChevronsDown, Languages, Sparkles } from "lucide-react";
import { LanguageCode, languages } from "./i18n";
import { MarketingCopy, marketingCopy } from "./marketing-copy";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
    PDFIX_AD_SLOTS?: Partial<Record<AdPlacement, string>>;
    googlefc?: {
      callbackQueue?: Array<{ CONSENT_API_READY: () => void }>;
      showRevocationMessage?: () => void;
    };
  }
}

type AdPlacement = "topMultiplex" | "betweenFeatures" | "betweenGuide";

const defaultSlots: Record<AdPlacement, string> = {
  topMultiplex: "2891380466",
  betweenFeatures: "4012890442",
  betweenGuide: "4012890442",
};

function AdSlot({
  placement,
  copy,
}: {
  placement: AdPlacement;
  copy: MarketingCopy;
}) {
  const slot = typeof window === "undefined"
    ? defaultSlots[placement]
    : window.PDFIX_AD_SLOTS?.[placement] || defaultSlots[placement];
  const isMultiplex = placement === "topMultiplex";

  useEffect(() => {
    if (!slot) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      console.warn("AdSense slot could not be initialized.", error);
    }
  }, [slot]);

  return (
    <aside className={`ad-placement ad-${placement}`} aria-label={copy.adLabel}>
      <div className="ad-explainer">
        <span>{copy.adLabel}{isMultiplex ? " · Multiplex" : ""}</span>
        <p>{copy.adSupport}</p>
      </div>
      {slot ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-9574409174549356"
          data-ad-slot={slot}
          data-ad-format={isMultiplex ? "autorelaxed" : "auto"}
          data-full-width-responsive={isMultiplex ? undefined : "true"}
        />
      ) : (
        <div className="ad-reserved">
          <strong>{copy.adFallback}</strong>
        </div>
      )}
    </aside>
  );
}

export function MarketingContent({
  language,
  onLanguageChange,
}: {
  language: LanguageCode;
  onLanguageChange: (language: LanguageCode) => void;
}) {
  const copy = marketingCopy[language];

  return (
    <div className="marketing-site">
      <div className="marketing-topbar">
        <a className="marketing-brand" href="#pdf-editor" aria-label="PDFix Studio">
          <span><Sparkles size={18} /></span>
          PDFix Studio
        </a>
        <label className="marketing-language">
          <Languages size={17} aria-hidden="true" />
          <span>{copy.language}</span>
          <select
            value={language}
            onChange={(event) => onLanguageChange(event.target.value as LanguageCode)}
            aria-label={copy.language}
          >
            {languages.map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}
          </select>
        </label>
      </div>

      <a className="initial-editor-cue" href="#pdf-editor" aria-label={copy.editorButton}>
        <span>{copy.editorLabel}</span>
        <div aria-hidden="true">
          <ChevronsDown />
          <ChevronsDown />
          <ChevronsDown />
        </div>
      </a>

      <AdSlot placement="topMultiplex" copy={copy} />

      <section className="seo-hero" aria-labelledby="free-pdf-editor">
        <div>
          <span className="seo-kicker">{copy.kicker}</span>
          <h1 id="free-pdf-editor">{copy.title}</h1>
          <p>{copy.intro}</p>
        </div>
        <div className="seo-trust-grid">
          {copy.trust.map((item) => (
            <div key={item.title}><strong>{item.title}</strong><span>{item.body}</span></div>
          ))}
        </div>
      </section>

      <AdSlot placement="betweenFeatures" copy={copy} />

      <section className="seo-section">
        <div className="section-heading">
          <span>{copy.featuresLabel}</span>
          <h2>{copy.featuresTitle}</h2>
          <p>{copy.featuresIntro}</p>
        </div>
        <div className="feature-cards">
          {copy.features.map((item) => (
            <article key={item.title}><h3>{item.title}</h3><p>{item.body}</p></article>
          ))}
        </div>
      </section>

      <section className="seo-section how-section">
        <div className="section-heading">
          <span>{copy.howLabel}</span>
          <h2>{copy.howTitle}</h2>
        </div>
        <ol className="steps">
          {copy.steps.map((item) => (
            <li key={item.title}><strong>{item.title}</strong><span>{item.body}</span></li>
          ))}
        </ol>
      </section>

      <AdSlot placement="betweenGuide" copy={copy} />

      <section className="seo-section split-section">
        <article>
          <span className="section-label">{copy.fontLabel}</span>
          <h2>{copy.fontTitle}</h2>
          {copy.fontParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </article>
        <article>
          <span className="section-label">{copy.privacyLabel}</span>
          <h2>{copy.privacyTitle}</h2>
          {copy.privacyParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </article>
      </section>

      <section className="seo-section faq-section" aria-labelledby="faq-title">
        <div className="section-heading">
          <span>{copy.faqLabel}</span>
          <h2 id="faq-title">{copy.faqTitle}</h2>
        </div>
        <div className="faq-grid">
          {copy.faq.map((item) => (
            <details key={item.title}><summary>{item.title}</summary><p>{item.body}</p></details>
          ))}
        </div>
      </section>

      <section className="editor-direction" aria-labelledby="editor-direction-title">
        <span>{copy.editorLabel}</span>
        <h2 id="editor-direction-title">{copy.editorTitle}</h2>
        <p>{copy.editorBody}</p>
        <a href="#pdf-editor">{copy.editorButton}</a>
        <div className="editor-arrows" aria-hidden="true">
          <ChevronsDown />
          <ChevronsDown />
          <ChevronsDown />
        </div>
      </section>
    </div>
  );
}

export function SiteFooter({ language }: { language: LanguageCode }) {
  const copy = marketingCopy[language];

  const openPrivacyChoices = () => {
    window.googlefc = window.googlefc || {};
    window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
    window.googlefc.callbackQueue.push({
      CONSENT_API_READY: () => window.googlefc?.showRevocationMessage?.(),
    });
  };

  return (
    <div className="marketing-site footer-wrap">
      <footer className="site-footer">
        <div><strong>PDFix Studio</strong><span>{copy.footerTagline}</span></div>
        <nav aria-label={copy.legalNav}>
          <a href="./terms.html">{copy.terms}</a>
          <a href="./privacy.html">{copy.privacyPolicy}</a>
          <a href="./cookies.html">{copy.cookiePolicy}</a>
          <button type="button" onClick={openPrivacyChoices}>{copy.cookieSettings}</button>
          <a href="./accessibility.html">{copy.accessibility}</a>
        </nav>
        <small>© {new Date().getFullYear()} PDFix Studio. {copy.copyright}</small>
      </footer>
    </div>
  );
}
